import type { MouseEvent } from 'react';
import { useMemo } from 'react';
import styled, { css } from 'styled-components';
import {
  AngleBoldLeft16,
  AngleBoldRight16,
  ArrowRight20
} from '@elfsight/icons';
import { Icon } from '@elfsight/lib-icon';
import { getColorWithAlpha } from '@elfsight/lib-utils';
import { createReadableClass, getInvertedColor } from '@elfsight/widget-utils';

export interface ArrowControlProps {
  direction: 'left' | 'right';
  size: number;
  ghost?: boolean;
  className?: string;
  backgroundColor?: string;
  backgroundColorOnHover?: string;
  iconColor?: string;
  iconColorOnHover?: string;
  iconVariant?: 'angle' | 'arrow';
  onClick?(e: MouseEvent<HTMLDivElement>): void;
}

export function ArrowControl({
  className,
  direction,
  ghost = false,
  size,
  backgroundColor = 'rgba(17, 17, 17, 0.5)',
  backgroundColorOnHover,
  iconColor,
  iconColorOnHover,
  iconVariant = 'angle',
  onClick
}: ArrowControlProps) {
  const [defaultBackgroundColor, hoverBackgroundColor] = useColors(
    ghost,
    backgroundColor,
    backgroundColorOnHover
  );

  const IconComponent = ICONS_MAPPING[iconVariant][direction];

  return (
    <ArrowControlContainer
      _direction={direction}
      _backgroundColor={defaultBackgroundColor}
      _hoverBackgroundColor={hoverBackgroundColor}
      _iconColor={iconColor || getInvertedColor(defaultBackgroundColor)}
      _iconColorOnHover={
        iconColorOnHover || iconColor || getInvertedColor(hoverBackgroundColor)
      }
      _ghost={ghost}
      className={className}
      style={{ width: size, height: size }}
      onClick={onClick}
    >
      <ArrowControlIcon
        component={IconComponent}
        size={size / 2}
        _withShadow={ghost}
      />
    </ArrowControlContainer>
  );
}

const ICONS_MAPPING = {
  angle: {
    left: AngleBoldLeft16,
    right: AngleBoldRight16
  },
  arrow: {
    left: ArrowRight20,
    right: ArrowRight20
  }
} as const;

const useColors = (
  ghost: boolean,
  backgroundColor: string,
  backgroundColorOnHover?: string
) =>
  useMemo(() => {
    let defaultBackgroundColor: string;
    let hoverBackgroundColor: string;

    if (ghost) {
      defaultBackgroundColor = getColorWithAlpha(backgroundColor, 0);
      hoverBackgroundColor = backgroundColor;
    } else {
      defaultBackgroundColor = backgroundColor;
      hoverBackgroundColor = getColorWithAlpha(backgroundColor, 1);
    }

    hoverBackgroundColor = backgroundColorOnHover || hoverBackgroundColor;

    return [defaultBackgroundColor, hoverBackgroundColor];
  }, [ghost, backgroundColor, backgroundColorOnHover]);

export const ArrowControlContainer = styled.div.attrs<{
  _direction: string;
  _backgroundColor: string;
  _hoverBackgroundColor: string;
  _iconColor: string;
  _iconColorOnHover: string;
  _ghost: boolean;
}>(({ _direction }) => ({
  className: createReadableClass({
    component: 'carousel',
    element: 'arrow-control',
    index: _direction
  })
}))`
  ${({
    _backgroundColor,
    _hoverBackgroundColor,
    _iconColor,
    _iconColorOnHover,
    _ghost
  }) => css`
    position: relative;
    display: block;
    padding: 0;
    border: none;
    outline: none;
    border-radius: 50% 50%;
    cursor: pointer;
    -webkit-tap-highlight-color: transparent;
    background-color: ${_backgroundColor};
    fill: ${_iconColor};
    box-shadow: ${_ghost ? '0 4px 12px 0 rgba(0, 0, 0, 0.3)' : 'none'};
    transition:
      background-color 0.3s ease,
      box-shadow 0.3s ease,
      fill 0.3s ease;

    &:hover {
      background-color: ${_hoverBackgroundColor};
      fill: ${_iconColorOnHover};
      box-shadow: ${_ghost ? '0 4px 12px 0 rgba(0, 0, 0, 0.5)' : 'none'};
    }

    &:active {
      box-shadow: ${_ghost ? '0 2px 6px 0 rgba(0, 0, 0, 0.5)' : 'none'};
    }
  `}
`;

export const ArrowControlIcon = styled(Icon).attrs({
  className: createReadableClass({
    component: 'carousel',
    element: 'arrow-control-icon'
  })
})<{ _withShadow: boolean }>`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);

  ${({ _withShadow }) =>
    _withShadow &&
    css`
      svg {
        filter: drop-shadow(0 0 2px rgba(0, 0, 0, 0.4));
      }
    `}
`;
